import { Area } from 'editor/src/store/design/operation/getNewElementDisplay';
import { MediaElement, MediaLine } from 'editor/src/store/design/types';
import { OutsideSheetWarning, WarningLevel, WarningType } from 'editor/src/store/editorModules/warnings/types';

import getPointPositionRotatedOnPoint from 'editor/src/util/getPointPositionRotatedOnPoint';

// mm. Auto nest packs copies flush against the margin and rotation produces long
// fractions, so a hair over the edge is float noise rather than a real overhang.
const EDGE_TOLERANCE_MM = 0.05;

// A rectangle lies inside an axis-aligned area exactly when all four of its corners do,
// so the rotated corners are all we need. Unlike the NotVisible warning, which tolerates
// an element until three quarters of it has left the print area, this is strict: any
// part sticking out counts (GAPI-53417).
export function isElementInsideArea(element: Exclude<MediaElement, MediaLine>, area: Area): boolean {
  const { x, y, width, height, r = 0 } = element;

  const corners: [number, number][] = [
    [x, y],
    getPointPositionRotatedOnPoint(x + width, y, x, y, r),
    getPointPositionRotatedOnPoint(x + width, y + height, x, y, r),
    getPointPositionRotatedOnPoint(x, y + height, x, y, r),
  ];

  const left = area.left - EDGE_TOLERANCE_MM;
  const top = area.top - EDGE_TOLERANCE_MM;
  const right = area.left + area.width + EDGE_TOLERANCE_MM;
  const bottom = area.top + area.height + EDGE_TOLERANCE_MM;

  return corners.every(([cx, cy]) => cx >= left && cx <= right && cy >= top && cy <= bottom);
}

// GAPI-53417: on a gang sheet every copy has to be printable, so any overhang is a
// problem the buyer must fix — there is no "mostly on the sheet". Lines are skipped, a
// gang sheet only ever carries artwork, and so are hidden elements: a warning the buyer
// cannot see or select on the canvas is one they cannot resolve.
function getOutsideSheetWarning(
  element: MediaElement,
  spreadIndex: number,
  area: Area,
): OutsideSheetWarning | undefined {
  if (element.type === 'line' || element.hidden || isElementInsideArea(element, area)) {
    return undefined;
  }

  return {
    type: WarningType.OutsideSheet,
    uuid: element.uuid,
    spreadIndex,
    level: WarningLevel.High,
  };
}

export default getOutsideSheetWarning;
